/**
 * Detects whether a notes value contains rich-text HTML. Legacy notes are
 * plain strings; once edited via Quill they become `<p>...</p>` etc.
 */
export function hasRichText(value: string | null | undefined): boolean {
  if (!value) return false;
  return /<[a-z][\s\S]*>/i.test(value);
}

const NAMED_ENTITIES: Record<string, string> = {
  nbsp: " ",
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  ndash: "\u2013",
  mdash: "\u2014",
  hellip: "\u2026",
  lsquo: "\u2018",
  rsquo: "\u2019",
  ldquo: "\u201C",
  rdquo: "\u201D",
  bull: "\u2022",
  middot: "\u00B7",
  deg: "\u00B0",
  times: "\u00D7",
  copy: "\u00A9",
  reg: "\u00AE",
  trade: "\u2122",
};

/** Decodes HTML entities (`&nbsp;`, `&amp;`, `&#39;`, `&#x2019;`) to the characters they stand for. */
export function decodeHtmlEntities(value: string): string {
  return value.replace(/&(#x[0-9a-f]+|#\d+|[a-z][a-z0-9]*);/gi, (whole, body: string) => {
    if (body[0] === "#") {
      const code = body[1] === "x" || body[1] === "X" ? parseInt(body.slice(2), 16) : parseInt(body.slice(1), 10);
      if (!Number.isFinite(code) || code < 0 || code > 0x10ffff) return whole;
      return code === 0xa0 ? " " : String.fromCodePoint(code);
    }
    return NAMED_ENTITIES[body.toLowerCase()] ?? whole;
  });
}

/** Tags that end a line or a block, so the words either side stay apart. */
const BREAKING_TAG = /<\/?(p|div|br|li|ul|ol|h[1-6]|blockquote|pre|tr|td|th|hr)\b[^>]*>/gi;

/**
 * Turns a rich-text notes string into plain text for previews and search:
 * block tags become spaces, other tags are dropped (so "<b>great</b>." stays
 * "great."), and entities such as `&nbsp;` are decoded. Quill writes `&nbsp;`
 * for spaces in pasted and list text, which showed up raw in search snippets.
 */
export function stripRichText(value: string | null | undefined): string {
  if (!value) return "";
  return decodeHtmlEntities(value.replace(BREAKING_TAG, " ").replace(/<[^>]*>/g, ""))
    .replace(/[\s\u00a0]+/g, " ")
    .trim();
}
